import React from "react";
import styled from "styled-components";
import { bodyType, colorTransition, focusRing, unselectable } from "./base/mixins";

export interface SegmentOption {
	value: string;
	label: React.ReactNode;
	disabled?: boolean;
	/** Accessible name and tooltip. Required in practice when `label` is an
	 *  icon only, or the segment has no name for a screen reader. */
	title?: string;
}

export interface SegmentedControlProps {
	options: SegmentOption[];
	value: string;
	onChange: (value: string) => void;
	/** Accessible name for the group, e.g. "Wall alignment". */
	label: string;
	className?: string;
	/** Each segment as wide as its label instead of all equally wide across the
	 *  container. For a control that sits in a row of other controls (the
	 *  projects gallery's header) rather than filling a panel. */
	fitContent?: boolean;
	/** `large` — compact control height (40px), 16px padding. For a
	 *  control that floats on its own over a canvas (the editor's Grundriss /
	 *  Einrichten switch) and must not read as condensed. */
	size?: "default" | "large";
}

/**
 * A form value — it picks one option for a setting.
 *
 * §8: **structurally different from a TabGroup; never substitute one for the
 * other.** Items are separated by a 6px gap, 30px tall, 6px radius, and the
 * inactive fill is panel grey rather than white — that last detail is what makes
 * the group read as one control rather than a row of tabs. Keep it.
 *
 * If you are changing what a panel shows, you want TabGroup.
 */
const SegmentedControl = ({
	options,
	value,
	onChange,
	label,
	className,
	fitContent = false,
	size = "default",
}: SegmentedControlProps) => (
	<Group role="group" aria-label={label} className={className} $fit={fitContent}>
		{options.map((option) => {
			const active = option.value === value;
			return (
				<Segment
					key={option.value}
					type="button"
					aria-pressed={active}
					aria-label={option.title}
					title={option.title}
					disabled={option.disabled}
					$active={active}
					$fit={fitContent}
					$large={size === "large"}
					onClick={() => onChange(option.value)}
				>
					{option.label}
				</Segment>
			);
		})}
	</Group>
);

const Group = styled.div<{ $fit: boolean }>`
	display: flex;
	gap: var(--space-segment-gap);
	width: ${({ $fit }) => ($fit ? "auto" : "100%")};
`;

const Segment = styled.button<{ $active: boolean; $fit: boolean; $large: boolean }>`
	${unselectable}
	flex: ${({ $fit }) => ($fit ? "none" : "1")};
	${({ $fit }) => $fit && "white-space: nowrap;"}
	min-width: 0;
	display: flex;
	align-items: center;
	justify-content: center;
	height: var(--size-segmented);
	/* 6px: between the 4px and 8px steps of the spacing scale. 8 made the
	   segments wide, 4 left the labels cramped. */
	padding: 0 6px;
	border: var(--border-default);
	border-radius: var(--radius-md);
	/* Panel grey, so an inactive segment reads as transparent against the panel. */
	background: var(--color-bg-panel);
	cursor: pointer;
	box-shadow: none;
	${bodyType}
	${colorTransition}
	${focusRing}

	${({ $large }) =>
		$large &&
		`
		height: var(--size-control-compact);
		padding: 0 var(--space-4);
	`}

	&:hover:not(:disabled) {
		background: var(--color-surface);
	}

	&:disabled {
		opacity: 0.45;
		cursor: not-allowed;
	}

	${({ $active }) =>
		$active &&
		`
		background: var(--color-secondary-fill);
		border-color: var(--color-secondary-fill);
		color: var(--color-text-on-secondary);

		&:hover:not(:disabled) {
			background: var(--color-secondary-fill-hover);
			border-color: var(--color-secondary-fill-hover);
		}
	`}
`;

export default SegmentedControl;
